import { Phone } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { useContent } from "@/content/ContentContext";
import { Eyebrow, Reveal } from "./Reveal";

export const FAQS = [
    {
        q: "Is AdvantaShield really accepted at dealership service departments?",
        a: "Yes — that's the entire point. AdvantaShield was created by dealership service directors specifically to be honored at the franchise service counter, and it's sold through dealers as well. When you need a repair, you hand your coverage to the service advisor and the claim starts there.",
    },
    {
        q: "How is a subscription different from a traditional extended warranty?",
        a: "Traditional contracts charge thousands of dollars upfront and lock you into multi-year terms. AdvantaShield works like the subscriptions you already manage: pay monthly, quarterly, or annually, manage everything through a customer portal, and cancel anytime without penalty.",
    },
    {
        q: "How do I file a claim?",
        a: "Tell the repair facility you're covered by AdvantaShield. They contact our claims team for authorization, eligible components and labor are approved, and the administrator pays the facility directly. You only pay your deductible, if one applies — regardless of how many items are repaired on that visit.",
    },
    {
        q: "Is my vehicle eligible?",
        a: "Most vehicles up to 15 model years old or with up to 150,000 miles qualify, and coverage can extend up to 185,000 miles. Your exact eligibility and rate are confirmed in under a minute when you get a quote.",
    },
    {
        q: "When does coverage begin?",
        a: "Coverage begins after a 30-day and 1,000-mile exclusionary period. Failures present before your subscription starts (pre-existing conditions) aren't eligible until they're repaired to manufacturer specifications — after that, covered components that fail during your active subscription are protected.",
    },
    {
        q: "Can I really cancel anytime?",
        a: "Yes. There are no locked-in multi-year terms and no cancellation penalties. Because you pay as you go, you stay covered only for as long as the coverage earns its place in your budget.",
    },
];

export const Faq = () => {
    const content = useContent();

    return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
                <Reveal className="lg:col-span-5">
                    <Eyebrow>{content.faq_eyebrow}</Eyebrow>
                    <h2 className="mt-5 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
                        {content.faq_heading_a}{" "}
                        <span className="text-sky-400">{content.faq_heading_b}</span>
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                        {content.faq_subtext}
                    </p>
                    <a
                        href="tel:+18886874889"
                        data-testid="faq-phone-link"
                        className="mt-8 inline-flex items-center gap-3.5 rounded-2xl border border-white/10 bg-[#121722] px-5 py-4 transition-colors duration-300 hover:border-sky-400/40"
                    >
                        <Phone className="h-5 w-5 text-sky-400" />
                        <span>
                            <span className="block text-xs text-slate-500">
                                Still have questions? Call us
                            </span>
                            <span className="block font-display text-base font-bold text-white">
                                (888) 687-4889
                            </span>
                        </span>
                    </a>
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-7">
                    <Accordion
                        type="single"
                        collapsible
                        data-testid="faq-accordion"
                        className="w-full"
                    >
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={f.q}
                                value={`item-${i}`}
                                className="border-white/10"
                            >
                                <AccordionTrigger
                                    data-testid={`faq-trigger-${i}`}
                                    className="py-6 text-left font-display text-base font-semibold text-white transition-colors duration-300 hover:text-sky-300 hover:no-underline md:text-lg"
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent
                                    data-testid={`faq-content-${i}`}
                                    className="text-sm leading-relaxed text-slate-400 md:text-base"
                                >
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </div>
        </div>
    </section>
    );
};
